/**
 * FlagsProvider — per-request evaluated feature flags (ADR 0150 P4;
 * epoch-25 trust boundary).
 *
 * No 'use client' directive needed — works in both SSR and browser.
 *
 * The value is the BACKEND-evaluated map of every `web_public`-surfaced
 * global flag — fetched by the server poll (`server/flags.ts`), threaded
 * through the loader seam, and serialized to `window.__FLAGS__` for
 * hydration. Targeting rules never left the backend; only booleans exist
 * on this side.
 *
 * `useFlag` is the ONLY read door, and its key parameter is the generated
 * `WebPublicGlobalFlagKey` union — reading an unregistered flag, a flag
 * that does not declare the `web_public` surface, or a
 * `targeting: subject` flag (whose decisions ride domain wires), is a
 * compile error.
 */
import type { EvaluatedWebFlags } from '@letsgethome/lib/flags/evaluate';
import type { WebPublicGlobalFlagKey } from '@letsgethome/lib/flags/registry_public_gen';
import { createSafeContext } from '@letsgethome/lib/react/create-safe-context';
import type { ReactNode } from 'react';

const [FlagsContextProvider, useFlags] = createSafeContext<EvaluatedWebFlags>('Flags');

/** Read one web_public global flag's evaluated value for this request. */
export function useFlag(key: WebPublicGlobalFlagKey): boolean {
  return useFlags()[key];
}

interface FlagsProviderProps {
  children: ReactNode;
  flags: EvaluatedWebFlags;
}

export function FlagsProvider({ children, flags }: FlagsProviderProps) {
  return <FlagsContextProvider value={flags}>{children}</FlagsContextProvider>;
}
